import { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, Upload, FileText, Trash2, Download, Eye, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sección de adjuntos (PDF firmado, evidencias) en la ficha de cierre PG-07. */
export function CierreAttachments({ projectId, token }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/cierre/attachments`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setItems(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const uploadFiles = async (files, kind = "cierre_firmado") => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const oversize = arr.filter(f => f.size > 10 * 1024 * 1024).map(f => f.name);
    if (oversize.length) { toast.error(`Máx 10 MB: ${oversize.join(", ")}`); return; }
    setUploading(true);
    try {
      if (arr.length === 1) {
        const fd = new FormData();
        fd.append("file", arr[0]);
        const r = await fetch(`${API}/pg07/projects/${projectId}/cierre/attachments?kind=${kind}`, {
          method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
        if (r.ok) { toast.success("Adjunto subido"); await load(); }
        else toast.error("Error subiendo archivo");
      } else {
        const fd = new FormData();
        arr.forEach(f => fd.append("files", f));
        const r = await fetch(`${API}/pg07/projects/${projectId}/cierre/attachments/bulk?kind=${kind}`, {
          method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
        if (r.ok) {
          const j = await r.json();
          toast.success(`${j.count} adjuntos subidos${j.errors?.length ? ` (${j.errors.length} con error)` : ""}`);
          await load();
        } else toast.error("Error subiendo archivos");
      }
    } finally { setUploading(false); }
  };

  const upload = async (event) => {
    await uploadFiles(event.target.files);
    event.target.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) await uploadFiles(e.dataTransfer.files);
  };

  const download = (a) => {
    const url = `${API}/pg07/projects/${projectId}/cierre/attachments/${a.id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const objUrl = window.URL.createObjectURL(b);
        const link = document.createElement("a");
        link.href = objUrl; link.download = a.filename;
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(objUrl);
      });
  };

  const preview = async (a) => {
    const url = `${API}/pg07/projects/${projectId}/cierre/attachments/${a.id}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("No se pudo cargar el preview"); return; }
    const b = await r.blob();
    const objUrl = window.URL.createObjectURL(b);
    setPreviewUrl(objUrl);
    setPreviewName(a.filename);
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName("");
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar ${a.filename}?`)) return;
    const r = await fetch(`${API}/pg07/projects/${projectId}/cierre/attachments/${a.id}`, {
      method: "DELETE", headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) { toast.success("Eliminado"); load(); }
    else toast.error("Error");
  };

  return (
    <Card data-testid="cierre-attachments-section">
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Adjuntos de cierre</h3>
            <p className="text-xs text-muted-foreground">Sube el PDF firmado escaneado u otras evidencias. Máx 10 MB.</p>
          </div>
          <label className="cursor-pointer" data-testid="cierre-upload-label">
            <input type="file" multiple className="hidden" accept="application/pdf,image/*" onChange={upload} data-testid="cierre-upload-input" disabled={uploading} />
            <Button size="sm" asChild>
              <span>{uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}Subir PDF(s)</span>
            </Button>
          </label>
        </div>

        <div
          onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
          onDrop={onDrop}
          className={`mb-3 border-2 border-dashed rounded-lg p-4 text-center text-xs transition ${dragActive ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" : "border-slate-200 dark:border-slate-700 text-muted-foreground"}`}
          data-testid="cierre-dropzone"
        >
          {dragActive ? "Suelta aquí para subir…" : "Arrastra uno o varios PDFs aquí (máx 10 MB cada uno)"}
        </div>

        {loading ? <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin" /></div> : items.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-3">Sin adjuntos aún.</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 p-2 rounded border hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`cierre-attachment-${i}`}>
                <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.filename}</div>
                  <div className="text-[10px] text-muted-foreground">{a.kind} · {Math.round(a.size_bytes / 1024)} KB · {a.uploaded_by} · {String(a.uploaded_at).slice(0, 10)}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => preview(a)} data-testid={`cierre-attachment-preview-${i}`}><Eye className="w-3.5 h-3.5 text-blue-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => download(a)} data-testid={`cierre-attachment-download-${i}`}><Download className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`cierre-attachment-delete-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!previewUrl} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-5xl h-[85vh] p-0" data-testid="cierre-attachment-preview-dialog">
          <DialogHeader className="p-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="truncate flex-1">{previewName}</span>
              <Button size="icon" variant="ghost" onClick={closePreview}><X className="w-4 h-4" /></Button>
            </DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title={previewName}
              className="w-full flex-1 border-0"
              style={{ height: "calc(85vh - 60px)" }}
              data-testid="cierre-attachment-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
